// Live events hook — the client half of the app-wide server→client push channel.
//
// Opens ONE long-lived authed SSE stream to `GET /api/events` and turns every
// store-change signal into a React Query invalidation, so any open dashboard
// reflects a mutation — from the agent, another device, the routine engine, or
// this same client — within a tick instead of on a poll timer.
//
// Mechanics mirror the proven chat-turn stream (chatStream.ts):
//   • apiFetch reader (not native EventSource) so the bearer token rides along
//     on the relay, and the relay device-prefix is resolved by BASE_URL.
//   • Each signal carries its seq as the SSE `id`; on reconnect we pass
//     `?since=<lastSeq>` so the server replays exactly what we missed. Seqs
//     belong to one server process (`hello.boot`): after a restart nothing can
//     be replayed, so a new boot id reloads every store and resets the baseline.
//   • A `gap` signal (our `since` predated the server buffer) → reload every
//     store. Duplicate invalidations are idempotent, so over-delivery is safe.
//   • visibility/online wake → immediate reconnect with a fresh backoff budget
//     (a backgrounded mobile tab silently drops its socket).
import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';

/**
 * Map each server store key to the React Query root key(s) it invalidates. A
 * root-key invalidation matches every nested query (e.g. `['projects','list',f]`
 * matches `['projects']`), so one entry per store covers all its views.
 */
const STORE_QUERY_KEYS: Record<string, string[][]> = {
  routines: [['routines'], ['routine-runs-all']],
  calendar: [['calendar']],
  contacts: [['contacts']],
  // The file tree's root is the active project's `meta.cwd` (server
  // `resolveActiveCwd`), so a project's details and a conversation's switch
  // both move it.
  projects: [['projects'], ['projects-meta'], ['files', 'cwd']],
  conversations: [['session-meta'], ['files', 'cwd']],
  ops: [['analytics'], ['alerts']],
  trash: [['trash']],
};

function invalidateStore(qc: QueryClient, store: string): void {
  const keys = STORE_QUERY_KEYS[store];
  if (!keys) return;
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
}

function invalidateAll(qc: QueryClient): void {
  for (const store of Object.keys(STORE_QUERY_KEYS)) invalidateStore(qc, store);
}

/** Backoff ceiling and base for reconnect (full-jitter, matches OneStone's retry doctrine). */
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/**
 * Subscribe to app-wide live store-change events for the lifetime of the
 * component. Mount once at the app root. No-op on the server (SSR guard).
 */
export function useLiveEvents(): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let controller: AbortController | null = null;
    let lastSeq = -1;
    /** The server process the seqs belong to (`hello.boot`); null before the first hello. */
    let boot: string | null = null;
    let retry = 0;
    let wakeRequested = false;

    const wake = (): void => {
      wakeRequested = true;
      controller?.abort();
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') wake();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', wake);

    const dispatch = (event: string, data: string): void => {
      if (event === 'change') {
        try {
          const parsed = JSON.parse(data) as { store?: string };
          if (parsed.store) invalidateStore(qc, parsed.store);
        } catch {
          /* a malformed frame must not break the stream */
        }
      } else if (event === 'gap') {
        invalidateAll(qc);
      } else if (event === 'hello') {
        // Adopt the server head as the baseline when the server process is new
        // to this client, so a later reconnect replays from connect-time. A
        // DIFFERENT process (the server restarted) restarted its seqs at 0:
        // what this client missed can't be replayed, so reload every store.
        try {
          const parsed = JSON.parse(data) as { seq?: number; boot?: string };
          if (typeof parsed.seq !== 'number') return;
          const serverBoot = parsed.boot ?? null;
          if (lastSeq < 0 || serverBoot !== boot) {
            if (lastSeq >= 0) invalidateAll(qc);
            lastSeq = parsed.seq - 1;
            boot = serverBoot;
          }
        } catch {
          /* ignore */
        }
      }
    };

    const readStream = async (signal: AbortSignal): Promise<void> => {
      const path = `/api/events${lastSeq >= 0 ? `?since=${lastSeq}` : ''}`;
      const res = await apiFetch(path, {
        headers: { Accept: 'text/event-stream' },
        signal,
      });
      if (!res.ok || !res.body) throw new Error(`events stream ${res.status}`);
      retry = 0; // a successful open resets the backoff budget

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let event = '';
      let dataLines: string[] = [];
      let id = '';

      while (!cancelled) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const raw of lines) {
          const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
          if (line.startsWith(':')) continue; // heartbeat comment
          if (line === '') {
            if (id) {
              const seq = parseInt(id, 10);
              if (Number.isFinite(seq) && seq > lastSeq) lastSeq = seq;
              id = '';
            }
            if (dataLines.length > 0) {
              dispatch(event, dataLines.join('\n'));
              dataLines = [];
            }
            event = '';
            continue;
          }
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
          else if (line.startsWith('id:')) id = line.slice(3).trim();
        }
      }
    };

    void (async () => {
      while (!cancelled) {
        const ac = new AbortController();
        controller = ac;
        try {
          await readStream(ac.signal);
        } catch {
          /* drop/reset — fall through to reconnect */
        } finally {
          controller = null;
        }
        if (cancelled) break;
        if (wakeRequested) {
          // Tab woke / network returned — reconnect immediately with a fresh budget.
          wakeRequested = false;
          retry = 0;
          continue;
        }
        retry += 1;
        const ceiling = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.min(retry, 5));
        const delay = Math.random() * ceiling; // full jitter
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    })();

    return () => {
      cancelled = true;
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', wake);
    };
  }, [qc]);
}
